import React, { useState, useEffect } from 'react';
import { useLanguage } from '../LanguageContext';
import { projLabel } from './UnitsDeals/constants';
import axios from 'axios';

// Shared lead presentation used by both the analytics member page and the stale-leads
// page so a lead looks identical everywhere: source + project + notes + full
// communication log + status history. Keep this the single source of truth.
export const STATUS_COLORS = { new: '#6366f1', contacted: '#3b82f6', interested: '#8b5cf6', site_visit_scheduled: '#f59e0b', site_visit_done: '#f97316', offer_sent: '#06b6d4', negotiating: '#ec4899', won: '#22c55e', lost: '#ef4444' };

export const SOURCE_LABELS = {
  snapchat: { en: 'Snapchat', ar: 'سنابشات' },
  snapchat_aqar: { en: 'Snapchat - Aqar', ar: 'سنابشات - عقار' },
  snapchat_site_visit: { en: 'Snapchat - Site Visit', ar: 'سنابشات - زار المشروع' },
  tiktok: { en: 'TikTok', ar: 'تيكتوك' },
  google_ads: { en: 'Google Ads', ar: 'إعلانات جوجل' },
  instagram: { en: 'Instagram', ar: 'انستغرام' },
  facebook: { en: 'Facebook', ar: 'فيسبوك' },
  aqar: { en: 'Aqar', ar: 'عقار' },
  site_visitor: { en: 'Site Visitor', ar: 'زار المشروع' },
  whatsapp: { en: 'WhatsApp', ar: 'واتساب' },
  website: { en: 'Website', ar: 'الموقع' },
  broker: { en: 'Broker', ar: 'وسيط' },
};
export const sourceLabel = (s, lang) => SOURCE_LABELS[s]?.[lang] || SOURCE_LABELS[s]?.en || (s || '').replace('_', ' ');

export const fmtWhen = (d) => {
  const x = new Date(d);
  return `${x.getDate()}/${x.getMonth() + 1} ${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`;
};

// ── Full lead story: notes + every comment + status history ──
export function LeadStory({ leadId, t }) {
  const { lang } = useLanguage();
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [full, tr] = await Promise.all([
          axios.get(`/api/leads/${leadId}`),
          axios.get(`/api/leads/${leadId}/transitions`),
        ]);
        if (alive) setD({ lead: full.data?.lead || {}, transitions: tr.data || [] });
      } catch { if (alive) setD({ lead: {}, transitions: [] }); }
    })();
    return () => { alive = false; };
  }, [leadId]);

  if (!d) return <div style={{ padding: 14, color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{t('loading')}</div>;
  // Every status change auto-logs a 'note' comm carrying a hidden <!--meta--> marker,
  // which just mirrors the status-history section below. Drop those so the comm log
  // shows only GENUINE communications (calls, directives, manual notes), not a copy
  // of سجل تغييرات الحالة.
  // Keep entries where the rep actually wrote a note (strip the hidden meta marker);
  // drop only the note-less auto-logs. Newest first.
  const comms = [...(d.lead.communications || [])]
    .filter(c => (c.message || '').replace(/\n?<!--meta:[\s\S]*?-->/g, '').trim())
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const transitions = [...(d.transitions || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const notes = (d.lead.notes || '').trim();
  const clean = (m) => (m || '').replace(/\n?<!--meta:[\s\S]*?-->/g, '').trim();
  return (
    <div style={{ padding: '12px 16px', background: 'var(--bg-primary)', borderTop: '1px solid var(--border)' }}>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>📡 {t('crmSource')}</span>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)', padding: '2px 9px', borderRadius: 7, background: 'var(--bg-secondary)' }}>{d.lead.source ? sourceLabel(d.lead.source, lang) : '—'}</span>
        </span>
        {d.lead.project && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>🏢 {t('crmProject')}</span>
            <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--accent)', padding: '2px 9px', borderRadius: 7, background: 'rgba(99,102,241,0.1)' }}>{projLabel(d.lead.project, t)}</span>
          </span>
        )}
      </div>
      {notes && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 4 }}>📝 {t('crmNotes') || 'Notes'}</div>
          <div style={{ fontSize: '0.82rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{notes}</div>
        </div>
      )}
      {Array.isArray(d.lead.relatedHistory) && d.lead.relatedHistory.length > 0 && (
        <div style={{ marginBottom: 12, padding: '10px 12px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8 }}>
          <div style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: '#b45309', marginBottom: 6 }}>
            🔗 {lang === 'ar' ? 'سجل سابق لنفس العميل (رقم مكرر)' : 'Previous records for this customer (same phone)'}
          </div>
          {d.lead.relatedHistory.map((rel, ri) => (
            <div key={rel.id || ri} style={{ marginBottom: ri < d.lead.relatedHistory.length - 1 ? 8 : 0, paddingBottom: ri < d.lead.relatedHistory.length - 1 ? 8 : 0, borderBottom: ri < d.lead.relatedHistory.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 700 }}>{rel.name}</span>
                <span style={{ fontSize: '0.7rem', padding: '1px 7px', borderRadius: 6, background: 'var(--bg-secondary)' }}>{sourceLabel(rel.source, lang)}</span>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: STATUS_COLORS[rel.status] || 'inherit' }}>{t('crmStatus_' + rel.status) || rel.status}</span>
                {rel.assignedTo && <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>· {rel.assignedTo}</span>}
              </div>
              {(rel.transitions || []).map((tr, i) => {
                let data = {}; try { data = typeof tr.data === 'string' ? JSON.parse(tr.data) : (tr.data || {}); } catch { /* ignore */ }
                return (
                  <div key={'t' + i} style={{ fontSize: '0.74rem', marginBottom: 2 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{t('crmStatus_' + tr.from_status)} → </span>
                    <strong style={{ color: STATUS_COLORS[tr.to_status] || 'inherit' }}>{t('crmStatus_' + tr.to_status)}</strong>
                    <span style={{ fontSize: '0.64rem', color: 'var(--text-secondary)' }}> · {fmtWhen(tr.created_at)}{tr.done_by ? ` · ${tr.done_by}` : ''}</span>
                    {(data.statusNote || data.lossDetails) && <div style={{ color: 'var(--text-secondary)', marginTop: 1 }}>{data.statusNote || data.lossDetails}</div>}
                  </div>
                );
              })}
              {(rel.communications || []).map((c, i) => {
                const m = (c.message || '').replace(/\n?<!--meta:[\s\S]*?-->/g, '').trim();
                return m ? <div key={'c' + i} style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: 1 }}>💬 {c.by ? <strong>{c.by}: </strong> : ''}{m}</div> : null;
              })}
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}>💬 {t('crmCommunicationLog')} ({comms.length})</div>
      {comms.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: 12 }}>{t('noData')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {comms.map((c, i) => {
            const isDir = c.type === 'directive', isReply = c.type === 'directive_reply';
            return (
              <div key={c.id || i} style={{ fontSize: '0.8rem', padding: '7px 10px', borderRadius: 8, background: isDir ? 'rgba(239,68,68,0.06)' : isReply ? 'rgba(34,197,94,0.06)' : 'var(--bg-secondary)', borderInlineStart: (isDir || isReply) ? `3px solid ${isDir ? '#ef4444' : '#22c55e'}` : '3px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                  <strong style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{isDir ? '🚩 ' : isReply ? '↩ ' : ''}{t('crmCommType_' + c.type) || c.type}{c.by ? ` · ${c.by}` : ''}</strong>
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{c.date ? fmtWhen(c.date) : ''}</span>
                </div>
                <div style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{clean(c.message)}</div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 6 }}>🔄 {t('crmStatusHistory')} ({transitions.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {transitions.map((tr, i) => {
          let data = {}; try { data = typeof tr.data === 'string' ? JSON.parse(tr.data) : (tr.data || {}); } catch { /* ignore */ }
          return (
            <div key={tr.id || i} style={{ fontSize: '0.78rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <strong>{t('crmStatus_' + tr.from_status)}</strong>
                <span style={{ color: 'var(--text-secondary)' }}>→</span>
                <strong style={{ color: STATUS_COLORS[tr.to_status] || 'inherit' }}>{t('crmStatus_' + tr.to_status)}</strong>
                <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>· {fmtWhen(tr.created_at)}{tr.done_by ? ` · ${tr.done_by}` : ''}</span>
              </div>
              {(data.statusNote || data.lossDetails) && (
                <div style={{ marginTop: 2, padding: '4px 9px', background: 'rgba(99,102,241,0.06)', borderRadius: 6, borderInlineStart: '3px solid var(--accent)', lineHeight: 1.5 }}>{data.statusNote || data.lossDetails}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
